/**
 * GatewayBusDeviceList — table of devices discovered on the RS485/CAN bus.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, HardDrive } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { GatewayDeviceConfig } from '@/types/gateway';

interface GatewayBusDeviceListProps {
  configs: GatewayDeviceConfig[];
  isLoading: boolean;
}

function getDeviceStatusDisplay(status: string): { dotColor: string; label: string } {
  switch (status) {
    case 'healthy':
      return { dotColor: 'bg-wt-green-primary', label: 'Healthy' };
    case 'degraded':
      return { dotColor: 'bg-wt-amber-primary', label: 'Degraded' };
    case 'unresponsive':
      return { dotColor: 'bg-wt-red-primary', label: 'Unresponsive' };
    default:
      return { dotColor: 'bg-gray-400', label: status };
  }
}

export function GatewayBusDeviceList({ configs, isLoading }: GatewayBusDeviceListProps) {
  if (isLoading) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-wt-text-secondary" />
        </CardContent>
      </Card>
    );
  }

  if (configs.length === 0) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
            <HardDrive className="w-4 h-4" />
            Bus Devices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-wt-text-secondary">
            No devices discovered. Check wiring and run a bus scan.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
          <HardDrive className="w-4 h-4" />
          Bus Devices ({configs.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-wt-text-secondary text-left">
                <th className="px-4 py-2 font-medium">Addr</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium text-right">Last Poll</th>
                <th className="px-4 py-2 font-medium text-right">Errors</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((config) => {
                const statusDisplay = getDeviceStatusDisplay(config.status);
                return (
                  <tr key={config.id} className="border-b border-border/50 hover:bg-wt-bg-tertiary/50">
                    <td className="px-4 py-2 font-mono-numeric">{config.bus_address}</td>
                    <td className="px-4 py-2 text-wt-text-primary">{config.equipment_type}</td>
                    <td className="px-4 py-2 text-wt-text-secondary">
                      {config.display_name ?? '--'}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${statusDisplay.dotColor}`} />
                        <span>{statusDisplay.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right text-wt-text-secondary">
                      {config.last_successful_poll
                        ? formatDistanceToNow(new Date(config.last_successful_poll), { addSuffix: true })
                        : 'Never'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span
                        className={`font-mono-numeric ${
                          config.consecutive_failures > 5
                            ? 'text-wt-red-primary'
                            : config.consecutive_failures > 0
                              ? 'text-wt-amber-primary'
                              : 'text-wt-text-secondary'
                        }`}
                      >
                        {config.consecutive_failures}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
